import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Header } from '../../components/layout/Header'
import { Card } from '../../components/common/Card'
import axios from 'axios'
import { useTheme } from '../../contexts/ThemeContext'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://192.168.0.98:8090/api'

interface SectorData {
  sector: string
  seller_count: number
  product_count: number
  total_revenue: number
  avg_order_value: number
  growth_rate: number
  top_countries: string[]
}

export const SectorsPage: React.FC = () => {
  const { colors } = useTheme()

  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics-sectors'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_BASE_URL}/markets/analytics/sectors`)
      return data
    },
  })

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
        <Header />
        <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #E0E0E0',
              borderTop: '4px solid #4A90E2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ color: colors.textSecondary }}>Loading sector analytics...</p>
          </div>
        </main>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
        <Header />
        <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
          <Card style={{ textAlign: 'center', padding: '60px' }}>
            <p style={{ color: '#EF4444' }}>Error loading data: {(error as Error).message}</p>
          </Card>
        </main>
      </div>
    )
  }

  const sectors: SectorData[] = data?.sectors || []

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      <Header />
      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '32px', marginBottom: '8px', color: colors.textPrimary }}>
            Product Sectors Analysis
          </h1>
          <p style={{ color: colors.textSecondary, fontSize: '16px' }}>
            Analyze performance across different product sectors from the Expandly database
          </p>
        </div>

        {/* Sectors Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
          {sectors.map((sector) => (
            <Card key={sector.sector} style={{ padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div>
                  <h3 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '4px', color: colors.textPrimary }}>
                    {sector.sector}
                  </h3>
                  <p style={{ fontSize: '14px', color: colors.textSecondary }}>
                    {sector.seller_count} sellers | {sector.product_count} products
                  </p>
                </div>
                <div style={{
                  padding: '6px 12px',
                  borderRadius: '20px',
                  backgroundColor: sector.growth_rate >= 0 ? '#50C87820' : '#EF444420',
                  color: sector.growth_rate >= 0 ? '#50C878' : '#EF4444',
                  fontWeight: 600,
                  fontSize: '14px',
                }}>
                  {sector.growth_rate >= 0 ? '+' : ''}{sector.growth_rate.toFixed(1)}%
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <p style={{ color: colors.textSecondary, fontSize: '13px', marginBottom: '4px' }}>Total Revenue</p>
                  <p style={{ fontSize: '24px', fontWeight: 700, color: colors.primary }}>
                    ${sector.total_revenue.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p style={{ color: colors.textSecondary, fontSize: '13px', marginBottom: '4px' }}>Avg Order Value</p>
                  <p style={{ fontSize: '24px', fontWeight: 700, color: '#7B68EE' }}>
                    ${sector.avg_order_value.toFixed(2)}
                  </p>
                </div>
              </div>

              <div>
                <p style={{ color: colors.textSecondary, fontSize: '13px', marginBottom: '8px' }}>Top Countries</p>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {(sector.top_countries || []).map(country => (
                    <span key={country} style={{
                      padding: '4px 10px',
                      backgroundColor: colors.background,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: colors.textPrimary,
                    }}>{country}</span>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </main>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
